import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getListViewState, saveListViewState, clearListViewState, isProfilePath } from '../utils/listViewState';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { FiMoreVertical, FiSearch, FiX } from 'react-icons/fi';
import { Spin } from 'antd';
import useBlockScrollThrough from '../hooks/useBlockScrollThrough';
import { LoadingOutlined } from '@ant-design/icons';
import PageDataLoader from 'components/pageDataLoader';
import useFillToViewport from '../hooks/useFillToViewport';
import { getStudentList, getStudentAutocomplete, updateStudentStatus, bulkUpdateStudentStatus, assignCourseBulk } from 'actions/studentManagement';
import { setUserDetails } from 'actions/userManagementV2';
import { getMarketSettingsData } from 'actions/studentMarketPlace';
import { Notification } from 'services';
import { COLORS } from '../utils/constants';
import { fluid } from '../utils/responsive';
import { exportToExcel } from '../utils/exportToExcel';
import { scrollToTableTop } from '../utils/scrollToTableTop';
import EnrollCourseModal from '../../../pages/userManagementV2/enrollCourseModal';
import UserActionConfirmModal from '../../../pages/userManagementV3/localComponent/userActionConfirmModal';
import DownloadFileModal from './DownloadFileModal';
import PageSizeRange from './PageSizeRange';
import GridIcon from '../../../Assets/userManagementV3/gridIcon.svg';
import ListIcon from '../../../Assets/userManagementV3/listIcon.svg';
import FilterIcon from '../../../pages/userManagementV2/dashboardV3/filter-line.svg';
import SortIcon from '../../../pages/userManagementV2/dashboardV3/swap-half-vertical-reverse.svg';
import DownloadIcon from '../../../pages/userManagementV2/dashboardV3/download.svg';
import ArrowLeft from '../../../pages/userManagementV2/dashboardV3/arrow-single-left.svg';
import ArrowRight from '../../../pages/userManagementV2/dashboardV3/arrow-single-right.svg';
import illNoData from '../../../Assets/ill-no-data.svg';

// Native hover title only when the text is actually cut off with "…".
const showTitleIfTruncated = (e) => {
    const el = e.currentTarget;
    el.title = el.scrollWidth > el.clientWidth ? el.textContent : '';
};

const COLUMN_TEMPLATE = `${fluid(36)} minmax(${fluid(220)},1.5fr) minmax(${fluid(180)},1.4fr) minmax(${fluid(320)},2fr) ${fluid(100)} ${fluid(100)}`;

const STATUS_PILLS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
];
// Course type filter — values are the backend's `courseType` enum, sent as
// the `courseType` query param of the student list API ("All" sends nothing).
const COURSE_TYPE_PILLS = [
    { value: 'All', label: 'All' },
    { value: 'course', label: 'Classroom course' },
    { value: 'videoResource', label: 'Video course' },
    { value: 'testSeries', label: 'Test series' },
    { value: 'studyMaterial', label: 'Study material' },
];
const SORT_PILLS = [
    { value: 'alphabetic', label: 'Alphabetic' },
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
];

// The API's real status enum is only "Approved" / "Suspended" / "Deleted" —
// there is no "Active" on the backend side. "Approved" is what an
// activated/active student record holds, normalized here to the UI's
// existing "Active" / "Inactive" / "Deleted" vocabulary.
const normalizeStatus = (value) => {
    const text = String(value || '').trim();
    if (!text) return 'Active';
    const normalized = text.toLowerCase();
    if (normalized === 'suspended') return 'Inactive';
    if (normalized === 'deleted') return 'Deleted';
    return 'Active';
};

// UI status ("Active"/"Inactive"/"Deleted") -> the real backend enum, for
// sending in API payloads (filter params, PATCH .../status body).
const toApiStatus = (uiStatus) => {
    if (uiStatus === 'Inactive') return 'Suspended';
    if (uiStatus === 'Deleted') return 'Deleted';
    return 'Approved';
};

const formatDate = (value) => {
    if (!value) return 'Joined recently';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Joined recently';
    return `Joined on ${new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date)}`;
};

// "+918789987899" -> "+91 8789987899" for the Contact column; anything not
// starting with +91 is shown as-is.
const formatContactPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return phone;
    const match = phone.trim().match(/^\+91\s*(\d+)$/);
    return match ? `+91 ${match[1]}` : phone;
};

const formatRoll = (roll) => {
    if (!roll) return '';
    const str = String(roll).trim();
    if (str.toLowerCase().startsWith('roll')) return str;
    return `Roll ${str}`;
};

const extractStudentCourses = (row) => {
    const list = (row.coursesList || [])
        .map((c) => (c.courseName && c.courseName !== '—' && c.courseName.toLowerCase() !== 'null' && c.courseName.toLowerCase() !== 'undefined' ? String(c.courseName).trim() : ''))
        .filter(Boolean);
    if (list.length > 0) return Array.from(new Set(list)).join('; ');
    if (row.course && row.course !== '—' && row.course.toLowerCase() !== 'null' && row.course.toLowerCase() !== 'undefined') {
        return String(row.course).trim();
    }
    return 'NA';
};

const extractStudentBatches = (row) => {
    const list = (row.coursesList || [])
        .map((c) => {
            const bName = c.batchName ? String(c.batchName).trim() : '';
            const sec = c.section ? String(c.section).trim() : '';
            if (bName && sec && !bName.toLowerCase().includes(sec.toLowerCase())) {
                return `${bName} - ${sec}`;
            }
            return bName;
        })
        .filter((b) => b && b !== '—' && b.toLowerCase() !== 'null' && b.toLowerCase() !== 'undefined');
    if (list.length > 0) return Array.from(new Set(list)).join('; ');
    if (row.batch && row.batch !== '—' && row.batch.toLowerCase() !== 'null' && row.batch.toLowerCase() !== 'undefined') {
        return String(row.batch).trim();
    }
    return 'NA';
};

const extractStudentRollNumber = (row) => {
    const clean = (val) => {
        if (val == null) return '';
        const s = String(val).replace(/^Roll\s*#?\s*/i, '').trim();
        return (s && s !== '—' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') ? s : '';
    };

    const list = (row.coursesList || [])
        .map((c) => clean(c.rollNumber))
        .filter(Boolean);
    if (list.length > 0) return Array.from(new Set(list)).join('; ');
    const fallback = clean(row.rollNumber);
    return fallback || 'NA';
};

const STUDENT_EXPORT_FIELDS = [
    { id: 'name', label: 'Name', getValue: (row) => (row.name && row.name !== '—' ? row.name : 'NA') },
    {
        id: 'phone', label: 'Phone Number', getValue: (row) => {
            const p = formatContactPhone(row.phone);
            return p && p !== '—' ? p : 'NA';
        }
    },
    { id: 'email', label: 'Email ID', getValue: (row) => (row.email && row.email !== '—' ? row.email : 'NA') },
    { id: 'course', label: 'Course', getValue: (row) => extractStudentCourses(row) },
    { id: 'batch', label: 'Batch', getValue: (row) => extractStudentBatches(row) },
    { id: 'rollNumber', label: 'Roll Number', getValue: (row) => extractStudentRollNumber(row) },
    { id: 'status', label: 'Status', getValue: (row) => (row.status && row.status !== '—' ? row.status : 'NA') },
    {
        id: 'joinedDate', label: 'Joined Date', getValue: (row) => {
            const d = (row.joinedOn || '').replace(/^Joined on\s*/i, '').trim();
            return d && d !== '—' ? d : 'NA';
        }
    },
];

const resolveAvatarUrl = (image) => {
    if (!image || typeof image !== 'string') return '';
    const trimmed = image.trim();
    if (!trimmed || trimmed.toLowerCase() === 'default.png' || trimmed.toLowerCase().includes('default.png') || trimmed.toLowerCase() === 'default.jpg') {
        return '';
    }
    return trimmed;
};

const mapCoursesList = (student) => {
    const basicprofile = student?.basicprofile || {};
    const rawCourses = Array.isArray(student?.courses) && student.courses.length > 0
        ? student.courses
        : (student?.courseName || student?.course?.name || student?.course || basicprofile?.courseName)
            ? [{
                courseName: student?.courseName || student?.course?.name || student?.course || basicprofile?.courseName || '—',
                courseType: student?.courseType || 'course',
                courseId: student?.courseId,
            }]
            : [];

    const rawBatches = Array.isArray(student?.batches) ? student.batches : [];
    const defaultRoll = student?.rollNumber || basicprofile?.rollNumber || basicprofile?.roll_no || basicprofile?.rollNo || '';

    if (rawCourses.length === 0) {
        const fallbackBatchName = student?.batchName || student?.standardName || student?.standard?.name || basicprofile?.batchName || basicprofile?.standardName || '';
        return [{
            courseName: '—',
            courseType: 'course',
            isLive: true,
            batchName: fallbackBatchName,
            rollNumber: defaultRoll,
        }];
    }

    return rawCourses.map((c) => {
        const cId = c.courseId || c.id;
        const cType = c.courseType || c.type || 'course';
        const isLive = cType === 'course' || cType === 'live';

        // Every batch of this course (a student can be in more than one).
        const courseBatches = rawBatches.filter((b) => String(b.courseId) === String(cId));
        const matchedBatch = courseBatches[0];

        let batchName = '';
        if (matchedBatch) {
            if (matchedBatch.batchName) {
                batchName = matchedBatch.batchName;
            } else if (matchedBatch.name) {
                batchName = matchedBatch.name;
            } else if (matchedBatch.std) {
                batchName = matchedBatch.std;
            }
        } else if (isLive && rawCourses.length === 1) {
            batchName = student?.batchName || student?.standardName || student?.standard?.name || basicprofile?.batchName || basicprofile?.standardName || '';
        }

        const section = matchedBatch?.section ? String(matchedBatch.section).trim() : '';

        // Per-batch roll no. from the API's batches[] (rollNo) takes priority.
        const rollNumber = matchedBatch?.rollNo || matchedBatch?.rollNumber || c.rollNumber || defaultRoll;

        // Batches after the first: shown as extra lines under the same course.
        const extraBatches = courseBatches.slice(1).map((b) => ({
            batchName: b.batchName || b.name || b.std || '',
            section: b.section ? String(b.section).trim() : '',
            rollNumber: b.rollNo || b.rollNumber || '',
        }));

        return {
            courseName: c.courseName || c.title || c.name || '—',
            courseType: cType,
            isLive,
            batchName,
            section,
            rollNumber,
            extraBatches,
        };
    });
};

// Identifies which batch/course the list is scoped to — the same
// standardId/courseId/courseType inputs fetchStudents sends — so the cached
// full list is never reused for a different batch/course.
// Id of the last newly-added student already confirmed by a fresh list
// fetch — module-level so it survives the table unmounting/remounting while
// navigating to Add Student and back. Prevents re-showing a stale
// lastAddedStudent (e.g. one deleted later).
let consumedAddedStudentId = null;

const getAddedStudentRecord = (lastAddedStudent) => {
    const raw = lastAddedStudent?.data ?? lastAddedStudent;
    return raw && (raw.id ?? raw._id ?? raw.userId) != null ? raw : null;
};

const getStudentListContextKey = (propStandardId, state) => JSON.stringify([
    propStandardId ?? state?.standardId ?? state?.batchId ?? null,
    state?.courseId ?? null,
    state?.courseType || state?.resourceType || null,
]);

const mapStudentRow = (student, index) => {
    const basicprofile = student?.basicprofile || {};
    const name = student?.name || basicprofile?.name || `Student ${index + 1}`;
    const phone = student?.phone || basicprofile?.phone || basicprofile?.mobile || '—';
    const email = student?.email || basicprofile?.email || '';
    const course = student?.courseName || student?.course?.name || basicprofile?.courseName || '—';
    const batch = student?.batchName || student?.standardName || student?.standard?.name || basicprofile?.batchName || basicprofile?.standardName || '—';
    const status = normalizeStatus(student?.status || student?.userStatus || basicprofile?.status || 'Active');
    const rollNumber = student?.rollNumber || basicprofile?.rollNumber || basicprofile?.roll_no || basicprofile?.rollNo || '';
    const rawImage = student?.avatar || student?.image || basicprofile?.image || basicprofile?.avatar || '';
    const avatar = resolveAvatarUrl(rawImage);
    const rawJoined = student?.createdAt || student?.joinedAt || basicprofile?.createdAt || basicprofile?.joinedAt;
    const joinedOn = formatDate(rawJoined);
    const coursesList = mapCoursesList(student);

    return {
        id: student?.id ?? student?._id ?? student?.userId ?? `${name}-${index}`,
        name,
        phone,
        email,
        course,
        batch,
        rollNumber,
        coursesList,
        status,
        joinedOn,
        joinedAt: rawJoined,
        avatar,
    };
};

// Shown after non-classroom course names: "Course name (Video course)".
const COURSE_TYPE_LABELS = { videoResource: 'Video course', studyMaterial: 'Study material', testSeries: 'Test series' };

const CourseBatchCell = React.memo(({ row, isExpanded, onToggleExpand }) => {
    // Same as the grid: drop the '—' placeholder mapCoursesList adds for
    // students with no course, so they show "No course assigned".
    const courses = (row.coursesList || []).filter((c) => c.courseName && c.courseName !== '—');
    if (courses.length === 0) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontSize: '13px', lineHeight: '20px', fontWeight: 400, color: '#AEAEAE', fontFamily: 'Roboto, sans-serif' }}>No course assigned</span>
            </div>
        );
    }

    const displayCourses = isExpanded ? courses : courses.slice(0, 1);
    const hiddenCount = courses.length - 1;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, padding: `6px ${fluid(85)} 6px 0`, boxSizing: 'border-box' }}>
            {displayCourses.map((item, idx) => {
                const isFirst = idx === 0;
                const isLive = item.isLive;
                const rollStr = formatRoll(item.rollNumber);

                // "batch - section | roll no" for one batch.
                const batchLine = (b) => {
                    const rollNo = b.rollNumber ? String(b.rollNumber).trim() : '';
                    const parts = [[b.batchName, b.section].filter(Boolean).join(' - '), rollNo].filter(Boolean);
                    return parts.length > 0 ? parts.join(' | ') : '';
                };
                let subTitle = '';
                let extraLines = [];
                if (isLive) {
                    extraLines = (item.extraBatches || []).map(batchLine).filter(Boolean);
                    // Live courses: "batch - section | roll no" (e.g. "users module - A | 1"),
                    // skipping any part that is missing.
                    const rollNo = item.rollNumber ? String(item.rollNumber).trim() : '';
                    const batchSection = [item.batchName, item.section].filter(Boolean).join(' - ');
                    const parts = [batchSection, rollNo].filter(Boolean);
                    subTitle = parts.length > 0 ? parts.join(' | ') : 'No batch assigned';
                } else {
                    if (rollStr) {
                        subTitle = rollStr;
                    }
                }

                return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {/* One line: a long name is cut with "…" so the course type stays beside it. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', minWidth: 0 }}>
                            <span onMouseEnter={showTitleIfTruncated} style={{
                                minWidth: 0,
                                fontSize: '13px',
                                lineHeight: '20px',
                                fontWeight: 400,
                                color: '#191919',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontFamily: 'Roboto, sans-serif'
                            }}>
                                {item.courseName}
                            </span>
                            {!isLive && COURSE_TYPE_LABELS[item.courseType] && (
                                <span style={{ fontSize: '13px', lineHeight: '20px', fontWeight: 400, color: '#AEAEAE', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'Roboto, sans-serif' }}>
                                    ({COURSE_TYPE_LABELS[item.courseType]})
                                </span>
                            )}

                            {!isExpanded && isFirst && hiddenCount > 0 && (
                                <span
                                    onClick={(e) => onToggleExpand(e)}
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        color: '#0085FF',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        fontFamily: 'Roboto, sans-serif'
                                    }}
                                >
                                    +{hiddenCount} more
                                </span>
                            )}
                        </div>

                        {subTitle && (
                            <span style={{
                                fontSize: '13px',
                                lineHeight: '20px',
                                fontWeight: 400,
                                color: '#AEAEAE',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontFamily: 'Roboto, sans-serif'
                            }}>
                                {subTitle}
                            </span>
                        )}
                        {extraLines.map((line, lineIdx) => (
                            <span key={lineIdx} style={{
                                fontSize: '13px',
                                lineHeight: '20px',
                                fontWeight: 400,
                                color: '#AEAEAE',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontFamily: 'Roboto, sans-serif'
                            }}>
                                {line}
                            </span>
                        ))}
                    </div>
                );
            })}

            {isExpanded && hiddenCount > 0 && (
                <span
                    onClick={(e) => onToggleExpand(e)}
                    style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#0085FF',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontFamily: 'Roboto, sans-serif',
                        marginTop: '2px'
                    }}
                >
                    - show less
                </span>
            )}
        </div>
    );
});
CourseBatchCell.displayName = 'CourseBatchCell';

// Same fallback pattern as StaffListTable.jsx's Avatar — shows the actual
// photo when present, otherwise a colored circle with the student's
// first+last initials instead of a generic placeholder image.
const AVATAR_COLORS = ['#594099', '#1982C4', '#8AC926', '#F5A623', '#FF6B6B', '#4ECDC4', '#D97706', '#059669'];

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
};

const Checkbox = React.memo(({ checked, indeterminate = false, onChange, ariaLabel, disabled = false }) => {
    const filled = !disabled && (checked || indeterminate);
    return (
        <span
            role="checkbox"
            aria-checked={indeterminate ? 'mixed' : checked}
            aria-disabled={disabled}
            aria-label={ariaLabel}
            onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                if (disabled) return;
                onChange && onChange(e);
            }}
            style={{
                width: fluid(24),
                height: fluid(24),
                borderRadius: fluid(6),
                border: `1.5px solid ${disabled ? '#D0D0D0' : (filled ? '#594099' : '#B3B3B3')}`,
                backgroundColor: disabled ? '#EFEFEF' : (filled ? '#594099' : '#FFFFFF'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                flexShrink: 0,
            }}
        >
            {!disabled && (
                indeterminate ? (
                    <svg width="12" height="10" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4H9" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                ) : checked && (
                    <svg width="12" height="10" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )
            )}
        </span>
    );
});
Checkbox.displayName = 'Checkbox';

const Avatar = React.memo(({ name, image }) => {
    const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];
    if (image) {
        return (
            <img
                src={image}
                alt={name}
                style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
        );
    }
    return (
        <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: `${color}1A`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 700,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
Avatar.displayName = 'Avatar';

// ---------- Grid view (same pattern as StaffListTable's GridCard) ----------

// 60px photo / initials for grid cards (falls back to initials if the photo fails).
const StudentGridAvatar = React.memo(({ name, image }) => {
    const [imgError, setImgError] = useState(false);
    const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];
    useEffect(() => { setImgError(false); }, [image]);
    if (image && !imgError) {
        return (
            <img src={image} alt={name} onError={() => setImgError(true)}
                style={{ width: fluid(60), height: fluid(60), borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        );
    }
    return (
        <div style={{ width: fluid(60), height: fluid(60), borderRadius: '50%', backgroundColor: `${color}1A`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Roboto, sans-serif', fontSize: fluid(24), fontWeight: 400, flexShrink: 0 }}>
            {getInitials(name)}
        </div>
    );
});
StudentGridAvatar.displayName = 'StudentGridAvatar';

// Status dot no longer shown on grid cards.
/*
const StudentStatusDot = ({ status }) => (
    <span style={{ width: fluid(8), height: fluid(8), borderRadius: '50%', backgroundColor: status === 'Active' ? '#00A566' : '#AEAEAE', flexShrink: 0, display: 'inline-block' }} />
);
*/

// "batch - section | roll no" for one batch (Classroom courses).
const gridBatchLine = (b) => {
    const rollNo = b?.rollNumber ? String(b.rollNumber).trim() : '';
    const parts = [[b?.batchName, b?.section].filter(Boolean).join(' - '), rollNo].filter(Boolean);
    return parts.join(' | ');
};

const realCourses = (row) => (row.coursesList || []).filter((c) => c.courseName && c.courseName !== '—');

const GRID_TEXT = { fontFamily: 'Roboto, sans-serif', letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const StudentGridCard = React.memo(({ row, checked, hovered, onHoverEnter, onHoverLeave, onToggleCheck, onToggleStatus, onDelete, onViewProfile, actionsDisabled = false }) => {
    const courses = realCourses(row);
    // Hover card lists every course (no "+N more").
    const shownCourses = courses;
    const cardBg = checked ? '#EDE9F6' : '#FFFFFF';

    // Collapsed card stays in normal flow (row height never changes); the
    // hover card is an overlay drawn on top of it, like the staff grid.
    return (
        <div style={{ width: '100%', position: 'relative', zIndex: hovered ? 100 : 1 }} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', padding: fluid(20), boxSizing: 'border-box', backgroundColor: cardBg, borderRadius: fluid(24), boxShadow: checked ? 'none' : '4px 4px 10px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: fluid(12) }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: fluid(12), minWidth: 0 }}>
                            <span onClick={() => onViewProfile(row)} style={{ ...GRID_TEXT, fontSize: fluid(16), color: '#191919', cursor: 'pointer' }}>{row.name}</span>
                            {/* Status dot removed from the grid card. */}
                            {/* <StudentStatusDot status={row.status} /> */}
                        </div>
                        <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(7), fontSize: fluid(14), color: '#636363' }}>{formatContactPhone(row.phone)}</span>
                        <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(4), fontSize: fluid(14), color: '#636363', minHeight: fluid(16) }}>{row.email}</span>
                    </div>
                    <StudentGridAvatar name={row.name} image={row.avatar} />
                </div>
                <div style={{ width: '100%', minWidth: 0, display: 'flex', alignItems: 'center', gap: fluid(8), marginTop: fluid(15) }}>
                    <span style={{ ...GRID_TEXT, fontSize: fluid(16), color: courses.length ? '#191919' : '#AEAEAE' }}>
                        {courses.length ? courses[0].courseName : 'No course assigned'}
                    </span>
                    {courses.length > 0 && !courses[0].isLive && COURSE_TYPE_LABELS[courses[0].courseType] && (
                        <span style={{ ...GRID_TEXT, overflow: 'visible', flexShrink: 0, fontSize: fluid(14), color: '#AEAEAE' }}>({COURSE_TYPE_LABELS[courses[0].courseType]})</span>
                    )}
                    {courses.length > 1 && (
                        <span style={{ ...GRID_TEXT, overflow: 'visible', fontSize: fluid(14), fontWeight: 700, color: '#0085FF', flexShrink: 0 }}>+{courses.length - 1} more</span>
                    )}
                </div>
            </div>

            {hovered && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', minHeight: `calc(200% + ${fluid(24)})`, zIndex: 200, padding: fluid(20), boxSizing: 'border-box', display: 'flex', flexDirection: 'column', backgroundColor: cardBg, borderRadius: fluid(24), boxShadow: checked ? 'none' : '0 7px 22px rgba(0,0,0,0.12)' }}>
                    <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'flex-start' }}>
                        <Checkbox checked={checked} onChange={() => onToggleCheck(row.id)} ariaLabel={`Select ${row.name}`} />
                        <div style={{ minWidth: 0, flex: 1, paddingLeft: fluid(18), paddingRight: fluid(8) }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: fluid(12), minWidth: 0 }}>
                                <span onClick={() => onViewProfile(row)} style={{ ...GRID_TEXT, fontSize: fluid(16), color: '#191919', cursor: 'pointer' }}>{row.name}</span>
                                {/* Status dot removed from the grid card. */}
                                {/* <StudentStatusDot status={row.status} /> */}
                            </div>
                            <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(7), fontSize: fluid(14), color: '#636363' }}>{formatContactPhone(row.phone)}</span>
                            {row.email && <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(4), fontSize: fluid(14), color: '#636363' }}>{row.email}</span>}
                            <span style={{ ...GRID_TEXT, display: 'block', marginTop: fluid(10), fontSize: fluid(14), color: '#AEAEAE' }}>{row.joinedOn}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <StudentGridAvatar name={row.name} image={row.avatar} />
                            <span onClick={() => onViewProfile(row)} style={{ marginTop: fluid(16), fontFamily: 'Roboto, sans-serif', color: '#1089FF', fontSize: fluid(14), fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>VIEW PROFILE</span>
                        </div>
                    </div>

                    <div style={{ marginTop: fluid(20), flex: 1, minWidth: 0 }}>
                        <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), color: '#AEAEAE' }}>Courses:</span>
                        {shownCourses.length === 0 ? (
                            <div style={{ ...GRID_TEXT, marginTop: fluid(6), fontSize: fluid(14), color: '#AEAEAE' }}>No course assigned</div>
                        ) : shownCourses.map((c, idx) => {
                            const lines = c.isLive ? [gridBatchLine(c), ...(c.extraBatches || []).map(gridBatchLine)].filter(Boolean) : [];
                            return (
                                <div key={idx} style={{ marginTop: fluid(8), minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: fluid(8), minWidth: 0 }}>
                                        <span onMouseEnter={showTitleIfTruncated} style={{ ...GRID_TEXT, minWidth: 0, fontSize: fluid(16), color: '#191919' }}>{c.courseName}</span>
                                        {!c.isLive && COURSE_TYPE_LABELS[c.courseType] && (
                                            <span style={{ ...GRID_TEXT, overflow: 'visible', flexShrink: 0, fontSize: fluid(14), color: '#AEAEAE' }}>({COURSE_TYPE_LABELS[c.courseType]})</span>
                                        )}
                                    </div>
                                    {c.isLive && lines.length === 0 && <div style={{ ...GRID_TEXT, fontSize: fluid(14), color: '#AEAEAE' }}>No batch assigned</div>}
                                    {lines.map((line, li) => (
                                        <div key={li} style={{ ...GRID_TEXT, fontSize: fluid(14), color: '#AEAEAE' }}>{line}</div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ marginTop: fluid(20), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {/* Disabled while rows are selected — the header's bulk SUSPEND / DELETE apply instead. */}
                        <button type="button" disabled={actionsDisabled} onClick={() => onToggleStatus(row)} style={{ border: 'none', background: 'transparent', padding: 0, fontSize: fluid(14), fontWeight: 700, cursor: actionsDisabled ? 'not-allowed' : 'pointer', opacity: actionsDisabled ? 0.4 : 1, color: row.status === 'Active' ? '#FFA931' : '#008A67' }}>
                            {row.status === 'Active' ? 'SUSPEND' : 'ACTIVATE'}
                        </button>
                        <button type="button" disabled={actionsDisabled} onClick={() => onDelete(row)} style={{ border: 'none', background: 'transparent', padding: 0, fontSize: fluid(14), fontWeight: 700, cursor: actionsDisabled ? 'not-allowed' : 'pointer', opacity: actionsDisabled ? 0.4 : 1, color: '#FF414D' }}>
                            DELETE
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});
StudentGridCard.displayName = 'StudentGridCard';

const PillGroup = React.memo(({ options, value, onChange }) => (
    <div style={{ display: "flex", gap: fluid(10), flexWrap: "wrap" }}>
        {options.map((opt) => {
            const isActive = opt.value === value;
            return (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    style={{
                        height: fluid(38),
                        padding: `0 ${fluid(18)}`,
                        borderRadius: fluid(1000),
                        border: isActive ? "none" : `1px solid #E6E6E6`,
                        backgroundColor: isActive ? "#594099" : "#FFFFFF",
                        color: isActive ? "#FFFFFF" : "#5A5A5A",
                        fontSize: fluid(14),
                        fontWeight: 500,
                        fontFamily: "Roboto, sans-serif",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    {opt.label}
                </button>
            );
        })}
    </div>
));
PillGroup.displayName = 'PillGroup';

const PopoverFooter = React.memo(({ onReset, onApply, disabled = false }) => (
    <>
        <div style={{ width: "100%", height: "1px", backgroundColor: "#E6E6E6", margin: `${fluid(20)} 0` }} />
        <div style={{ display: "flex", justifyContent: "space-between", gap: fluid(12) }}>
            <button
                type="button"
                onClick={disabled ? undefined : onReset}
                disabled={disabled}
                style={{
                    height: fluid(40),
                    // Keep the label centred regardless of page button CSS.
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    boxSizing: "border-box",
                    padding: `0 ${fluid(20)}`,
                    borderRadius: fluid(10),
                    border: "1.5px solid #594099",
                    backgroundColor: "transparent",
                    color: "#594099",
                    fontSize: fluid(14),
                    fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                RESET
            </button>
            <button
                type="button"
                onClick={disabled ? undefined : onApply}
                disabled={disabled}
                style={{
                    height: fluid(40),
                    // Keep the label centred regardless of page button CSS.
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    boxSizing: "border-box",
                    padding: `0 ${fluid(28)}`,
                    borderRadius: fluid(10),
                    border: "none",
                    backgroundColor: "#594099",
                    color: "#FFFFFF",
                    fontSize: fluid(14),
                    fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                APPLY
            </button>
        </div>
    </>
));
PopoverFooter.displayName = 'PopoverFooter';

const StudentListTable = React.memo(({ isMobile = false, onAddStudent, studentList, studentListTotal, lastAddedStudent, getStudentList, getStudentAutocomplete, updateStudentStatus, bulkUpdateStudentStatus, assignCourseBulk, setUserDetails, standardId: propStandardId, onStudentStatusChange, marketSettingData, getMarketSettingsData }) => {
    const history = useHistory();
    const location = useLocation();

    // Course list page for a video / test series / study material course:
    // ADD STUDENT only if that course type is enabled in market settings
    // (same flags as the enroll course modal). Classroom / dashboard: unchanged.
    const pageCourseType = location.state?.courseType || location.state?.resourceType;
    const MARKET_FLAG_BY_TYPE = { videoResource: 'videoResourceSelected', testSeries: 'onlineTestSelected', studyMaterial: 'studyMaterialSelected' };
    const marketFlag = MARKET_FLAG_BY_TYPE[pageCourseType];
    useEffect(() => {
        if (marketFlag) getMarketSettingsData?.(true);
    }, [marketFlag, getMarketSettingsData]);
    const marketSettings = marketSettingData?.data ?? marketSettingData;
    const canAddStudent = !marketFlag || marketSettings?.[marketFlag] === true;
    const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [assignCourseModalOpen, setAssignCourseModalOpen] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    // Search / filter / sort applied before opening a profile (restored on return).
    const viewStateKey = `students:${getStudentListContextKey(propStandardId, location.state)}`;
    const viewStateKeyRef = useRef(viewStateKey);
    viewStateKeyRef.current = viewStateKey;
    const [restoredView] = useState(() => getListViewState(viewStateKey));
    const [search, setSearch] = useState(restoredView?.search ?? '');
    const [studentItems, setStudentItems] = useState([]);
    // Which search/status `studentItems` was fetched for — while it differs
    // from the current search/status (API still in flight), rows are
    // filtered locally so the table updates instantly, like StaffListTable.
    const [itemsQuery, setItemsQuery] = useState({ search: '', status: 'Active', courseType: 'All' });
    // Full (unsearched, unfiltered) list for the current batch/course
    // context — the source for that instant local filtering.
    const [allStudentsCache, setAllStudentsCache] = useState({ key: null, items: [] });
    // Every student (all statuses) for the current batch/course context —
    // loaded once in the background so switching the status filter (Inactive /
    // All) can show rows instantly instead of "no data" first.
    const [allStatusCache, setAllStatusCache] = useState({ key: null, items: [] });
    // False until this mount's first list fetch lands — while false, a
    // just-added student (lastAddedStudent) is shown on top immediately.
    const [hasFreshFetch, setHasFreshFetch] = useState(false);
    const lastAddedStudentRef = useRef(lastAddedStudent);
    lastAddedStudentRef.current = lastAddedStudent;
    const [expandedRows, setExpandedRows] = useState({});
    const [statusFilter, setStatusFilter] = useState(restoredView?.statusFilter ?? 'Active');
    const [sortBy, setSortBy] = useState(restoredView?.sortBy ?? 'newest');
    const [page, setPage] = useState(1);
    // Rows per page — picked from the "1-20 of N" range text.
    const [limit, setLimit] = useState(20);
    // List / grid view (toggle next to the pager), like the staff list.
    const [viewMode, setViewMode] = useState(restoredView?.viewMode ?? 'list');
    const [hoveredCardId, setHoveredCardId] = useState(null);
    // Nothing to show until the first fetch lands (section/course lists, or the
    // main list with no saved copy) — start with the loader on so "no data"
    // never flashes before the students arrive.
    const [isLoading, setIsLoading] = useState(() => {
        const isMain = getStudentListContextKey(propStandardId, location.state) === getStudentListContextKey(undefined, undefined);
        return !(isMain && Array.isArray(studentList) && studentList.length > 0);
    });
    // False until this mount's first list request answers — until then the
    // rows area shows a loader instead of an old (redux) copy of the list.
    // Searching / filtering later never brings it back.
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    // First-load spinner area stretches to the window bottom (level with the sidebar).
    const initialLoaderRef = useRef(null);
    const initialLoaderHeight = useFillToViewport(initialLoaderRef, !initialLoadDone);
    const [filterOpen, setFilterOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const [draftStatusFilter, setDraftStatusFilter] = useState(restoredView?.statusFilter ?? 'Active');
    // Sent as `courseType` (see COURSE_TYPE_PILLS); 'All' sends nothing.
    const [courseTypeFilter, setCourseTypeFilter] = useState(restoredView?.courseTypeFilter ?? 'All');
    const [draftCourseTypeFilter, setDraftCourseTypeFilter] = useState(restoredView?.courseTypeFilter ?? 'All');

    useEffect(() => {
        saveListViewState(viewStateKeyRef.current, { search, statusFilter, courseTypeFilter, sortBy, viewMode });
    }, [search, statusFilter, courseTypeFilter, sortBy, viewMode]);

    // Keep the saved view only when leaving for a profile page.
    useEffect(() => () => {
        if (!isProfilePath(history.location?.pathname)) clearListViewState(viewStateKeyRef.current);
    }, [history]);
    const [draftSortBy, setDraftSortBy] = useState(sortBy);
    const filterRef = useRef(null);
    const sortRef = useRef(null);
    // Open Filter/Sort popover panels — scrolling with the cursor over them
    // must not scroll the table behind (see useBlockScrollThrough).
    const filterPanelRef = useRef(null);
    const sortPanelRef = useRef(null);
    useBlockScrollThrough(filterPanelRef, filterOpen);
    useBlockScrollThrough(sortPanelRef, sortOpen);
    const tableRootRef = useRef(null);
    const rowsRegionRef = useRef(null);

    // Changing page / rows per page: start the new page from its first row.
    const skipFirstPageScrollRef = useRef(true);
    useEffect(() => {
        if (skipFirstPageScrollRef.current) { skipFirstPageScrollRef.current = false; return; }
        scrollToTableTop(tableRootRef.current, rowsRegionRef.current);
    }, [page, limit]);
    const activeRequestIdRef = useRef(0);
    const fetchStudentsRef = useRef(null);
    // Latest list size for fetchStudents' loader check, without adding
    // studentItems to its deps (which would re-trigger the fetch effects).
    const studentItemsCountRef = useRef(0);
    // Batch/course context whose full list has already loaded once — after
    // that, refetches (e.g. clearing a search that matched nothing) never
    // show the loader even if the visible list is empty.
    const loadedContextKeyRef = useRef(null);

    const toggleExpandRow = useCallback((rowId, e) => {
        if (e) e.stopPropagation();
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    }, []);

    // Nearest scrolling parent of the table (the page's .dv2-outer-scroll) —
    // scroll only that, never the app layout around it (scrollIntoView moved
    // every ancestor, pushing the first row under the sticky header).
    const getScrollParent = (el) => {
        let node = el?.parentElement;
        while (node && node !== document.body) {
            const { overflowY } = window.getComputedStyle(node);
            if (overflowY === 'auto' || overflowY === 'scroll') return node;
            node = node.parentElement;
        }
        return null;
    };
    // Distance from the table top to the scroll area's top (>0: table is below it).
    const getTableOffset = () => {
        const el = tableRootRef.current;
        const parent = getScrollParent(el);
        if (!el || !parent) return { parent: null, delta: 0 };
        return { parent, delta: el.getBoundingClientRect().top - parent.getBoundingClientRect().top };
    };

    const scrollTableIntoView = useCallback(() => {
        const { parent, delta } = getTableOffset();
        if (!parent || Math.abs(delta) < 4) return Promise.resolve();
        parent.scrollBy({ top: delta, behavior: 'smooth' });
        return new Promise((resolve) => setTimeout(resolve, 450));
    }, []);

    const extractItemsFromResponse = (res) => {
        if (!res) return [];
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res)) return res;
        return [];
    };

    const fetchStudents = useCallback((callback) => {
        const trimmed = search.trim();
        const activeStandardId = propStandardId ?? location.state?.standardId ?? location.state?.batchId;
        const activeCourseId = location.state?.courseId;
        const activeCourseType = location.state?.courseType || location.state?.resourceType;
        const contextKey = getStudentListContextKey(propStandardId, location.state);
        const applyResult = (items) => {
            // A fresh list now includes any just-added student — stop
            // showing it from lastAddedStudent.
            const added = getAddedStudentRecord(lastAddedStudentRef.current);
            if (added) consumedAddedStudentId = String(added.id ?? added._id ?? added.userId);
            setHasFreshFetch(true);
            setStudentItems(items);
            setItemsQuery({ search: trimmed, status: statusFilter, courseType: courseTypeFilter });
            if (!trimmed && statusFilter === 'Active' && courseTypeFilter === 'All') {
                setAllStudentsCache({ key: contextKey, items });
            }
            loadedContextKeyRef.current = contextKey;
        };

        const params = { page: 1, limit: 1000 };
        if (activeStandardId && activeStandardId !== -1 && activeStandardId !== '-1') {
            params.standardId = activeStandardId;
        }
        if (activeCourseId && activeCourseId !== -1 && activeCourseId !== '-1') {
            params.courseId = activeCourseId;
        }
        if (activeCourseType) {
            params.courseType = activeCourseType === 'live' ? 'course' : activeCourseType;
        }
        if (statusFilter !== 'All') params.status = toApiStatus(statusFilter);
        // Course type filter (overrides a page-level course type when applied).
        if (courseTypeFilter !== 'All') params.courseType = courseTypeFilter;
        if (trimmed) {
            params.search = trimmed;
        }
        if (sortBy === 'alphabetic') {
            params.sort = 'alphabetic';
            params.sortBy = 'alphabetic';
        } else if (sortBy === 'oldest') {
            params.sort = 'oldest';
            params.sortBy = 'oldest';
        } else {
            params.sort = 'newest';
            params.sortBy = 'newest';
        }

        const requestId = ++activeRequestIdRef.current;
        // Same rule as StaffListTable: never show the loader while
        // searching, filtering or sorting — only on the initial (empty) load.
        const isSearchOrFilterOrSort = Boolean(trimmed || statusFilter !== 'Active' || courseTypeFilter !== 'All' || sortBy !== 'newest');
        // Only the default view is stored in redux (see StaffListTable).
        if (isSearchOrFilterOrSort) params.skipRedux = true;
        const hasLoadedThisContext = loadedContextKeyRef.current === contextKey;
        if (!isSearchOrFilterOrSort && studentItemsCountRef.current === 0 && !hasLoadedThisContext) {
            setIsLoading(true);
        }
        const safety = setTimeout(() => {
            if (requestId === activeRequestIdRef.current) {
                setIsLoading(false);
            }
        }, 5000);
        getStudentList?.(params, (response, error) => {
            if (requestId !== activeRequestIdRef.current) return;
            if (error || !response) {
                clearTimeout(safety);
                setIsLoading(false);
                setInitialLoadDone(true);
                callback?.();
                return;
            }
            const list = extractItemsFromResponse(response);
            const totalCount = Number(response?.total ?? response?.totalItems ?? list.length ?? 0);

            // If backend returned fewer items than total and capped limit, fetch remaining pages to ensure
            // the full dataset is available for complete multi-page sorting and pagination.
            if (list.length < totalCount && list.length > 0) {
                const pageSize = list.length;
                const totalPagesCount = Math.ceil(totalCount / pageSize);
                const pagePromises = [];
                for (let p = 2; p <= totalPagesCount; p++) {
                    pagePromises.push(
                        new Promise((resolve) => {
                            getStudentList?.({ ...params, page: p, limit: pageSize }, (pageRes) => {
                                resolve(extractItemsFromResponse(pageRes));
                            });
                        })
                    );
                }
                Promise.all(pagePromises).then((results) => {
                    if (requestId !== activeRequestIdRef.current) return;
                    clearTimeout(safety);
                    setIsLoading(false);
                    setInitialLoadDone(true);
                    const combined = [...list, ...results.flat()];
                    applyResult(combined);
                    callback?.();
                }).catch(() => {
                    if (requestId !== activeRequestIdRef.current) return;
                    clearTimeout(safety);
                    setIsLoading(false);
                    setInitialLoadDone(true);
                    applyResult(list);
                    callback?.();
                });
            } else {
                clearTimeout(safety);
                setIsLoading(false);
                setInitialLoadDone(true);
                applyResult(list);
                callback?.();
            }
        });
    }, [search, statusFilter, courseTypeFilter, sortBy, getStudentList, propStandardId, location.state]);

    useEffect(() => {
        fetchStudentsRef.current = fetchStudents;
    }, [fetchStudents]);

    useEffect(() => {
        const contextKey = getStudentListContextKey(propStandardId, location.state);
        const activeStandardId = propStandardId ?? location.state?.standardId ?? location.state?.batchId;
        const activeCourseId = location.state?.courseId;
        const activeCourseType = location.state?.courseType || location.state?.resourceType;
        const params = { page: 1, limit: 1000, sort: 'newest', sortBy: 'newest', skipRedux: true };
        if (activeStandardId && activeStandardId !== -1 && activeStandardId !== '-1') params.standardId = activeStandardId;
        if (activeCourseId && activeCourseId !== -1 && activeCourseId !== '-1') params.courseId = activeCourseId;
        if (activeCourseType) params.courseType = activeCourseType === 'live' ? 'course' : activeCourseType;
        let cancelled = false;
        getStudentList?.(params, (response, error) => {
            if (cancelled || error || !response) return;
            setAllStatusCache({ key: contextKey, items: extractItemsFromResponse(response) });
        });
        return () => { cancelled = true; };
    }, [propStandardId, location.state, getStudentList]);

    useEffect(() => {
        studentItemsCountRef.current = studentItems.length;
    }, [studentItems]);

    const refetchList = useCallback((callback) => {
        fetchStudentsRef.current?.(callback);
    }, []);

    useEffect(() => {
        if (page !== 1) {
            setPage(1);
        }
    }, [search, statusFilter, courseTypeFilter, sortBy, propStandardId, location.state]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchStudentsRef.current?.();
        }, search.trim() ? 300 : 0);
        return () => clearTimeout(timer);
    }, [search, statusFilter, courseTypeFilter, sortBy, propStandardId, location.state]);

    const currentContextKey = getStudentListContextKey(propStandardId, location.state);
    // Redux `studentList` only ever holds the main (unscoped) list's last fetch
    // — a section/course list must not show it, or the previous section's
    // students flash until this one's fetch lands.
    const isMainList = currentContextKey === getStudentListContextKey(undefined, undefined);

    useEffect(() => {
        // Only before this mount's first fetch lands — after that an empty
        // result (filter/search with no match) must stay empty.
        if (!hasFreshFetch && isMainList && !search.trim() && studentItems.length === 0 && Array.isArray(studentList) && studentList.length > 0) {
            setStudentItems(studentList);
        }
    }, [hasFreshFetch, isMainList, studentList, search, studentItems.length]);

    const isFilterDisabled = statusFilter === 'Active' && draftStatusFilter === 'Active'
        && courseTypeFilter === 'All' && draftCourseTypeFilter === 'All';
    const isSortDisabled = sortBy === 'newest' && draftSortBy === 'newest';

    useEffect(() => {
        if (!filterOpen) return undefined;
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setDraftStatusFilter(statusFilter);
                setDraftCourseTypeFilter(courseTypeFilter);
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filterOpen, statusFilter, courseTypeFilter]);

    useEffect(() => {
        if (!sortOpen) return undefined;
        const handleClickOutside = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) {
                setDraftSortBy(sortBy);
                setSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [sortOpen, sortBy]);

    useEffect(() => {
        if (openMenuId === null) return undefined;
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openMenuId]);

    // Ticking a row closes its open ⋮ menu (it's locked while checked).
    useEffect(() => {
        if (openMenuId !== null && selectedIds.has(openMenuId)) setOpenMenuId(null);
    }, [selectedIds, openMenuId]);

    // ⋮ menu opens upward when there isn't room below (last rows).
    const [menuOpenUp, setMenuOpenUp] = useState(false);
    const toggleRowMenu = useCallback((rowId, buttonEl) => {
        if (openMenuId === rowId) { setOpenMenuId(null); return; }
        let clipBottom = window.innerHeight;
        for (let node = buttonEl?.parentElement; node && node !== document.body; node = node.parentElement) {
            const { overflowY } = window.getComputedStyle(node);
            if (overflowY !== 'visible') { clipBottom = Math.min(clipBottom, node.getBoundingClientRect().bottom); break; }
        }
        const spaceBelow = clipBottom - (buttonEl?.getBoundingClientRect().bottom || 0);
        setMenuOpenUp(spaceBelow < 170);
        setOpenMenuId(rowId);
    }, [openMenuId]);

    const openFilterPopover = useCallback(() => {
        setDraftStatusFilter(statusFilter);
        setDraftCourseTypeFilter(courseTypeFilter);
        setFilterOpen((o) => !o);
    }, [statusFilter, courseTypeFilter]);

    const applyFilterPopover = useCallback(() => {
        setStatusFilter(draftStatusFilter);
        setCourseTypeFilter(draftCourseTypeFilter);
        setPage(1);
        setFilterOpen(false);
    }, [draftStatusFilter, draftCourseTypeFilter]);

    const resetFilterPopover = useCallback(() => {
        setDraftStatusFilter('Active');
        setStatusFilter('Active');
        setDraftCourseTypeFilter('All');
        setCourseTypeFilter('All');
        setPage(1);
        setFilterOpen(false);
    }, []);

    const openSortPopover = useCallback(() => {
        setDraftSortBy(sortBy);
        setSortOpen((o) => !o);
    }, [sortBy]);

    const applySortPopover = useCallback(() => {
        setSortBy(draftSortBy);
        setPage(1);
        setSortOpen(false);
    }, [draftSortBy]);

    const resetSortPopover = useCallback(() => {
        setDraftSortBy('newest');
        setSortBy('newest');
        setPage(1);
        setSortOpen(false);
    }, []);

    const normalizedRows = useMemo(() => {
        // Redux list only fills the table until this mount's first fetch lands —
        // after that an empty result (search with no match) must stay empty.
        let base = (studentItems.length > 0 || hasFreshFetch)
            ? studentItems
            : (isMainList && Array.isArray(studentList) && studentList.length > 0 ? studentList : []);
        base = Array.isArray(base) ? base : [];
        // Just came back from Add Student: until this mount's first fresh
        // fetch lands, show the new student on top of the previously loaded
        // list right away (main student list only — not batch/course lists).
        const added = getAddedStudentRecord(lastAddedStudent);
        if (added && isMainList && !hasFreshFetch) {
            const addedId = String(added.id ?? added._id ?? added.userId);
            const alreadyListed = base.some((s) => String(s?.id ?? s?._id ?? s?.userId) === addedId);
            if (addedId !== consumedAddedStudentId && !alreadyListed) {
                base = [added, ...base];
            }
        }
        return base.map((student, index) => mapStudentRow(student, index));
    }, [studentItems, studentList, lastAddedStudent, hasFreshFetch, isMainList]);
    const cachedRows = useMemo(() => (
        allStudentsCache.key === currentContextKey
            ? allStudentsCache.items.map((student, index) => mapStudentRow(student, index))
            : []
    ), [allStudentsCache, currentContextKey]);

    const allStatusRows = useMemo(() => (
        allStatusCache.key === currentContextKey
            ? allStatusCache.items.map((student, index) => mapStudentRow(student, index))
            : []
    ), [allStatusCache, currentContextKey]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        // API result for the current search/status not in yet — filter the
        // full cached list (or what's loaded) locally so the table updates
        // immediately; once it lands, its result is shown as returned.
        const isPending = itemsQuery.search !== search.trim() || itemsQuery.status !== statusFilter
            || itemsQuery.courseType !== courseTypeFilter;
        let next = [...normalizedRows];
        if (isPending) {
            // The cache only holds the Active list (it's saved for the default
            // view), so for other status filters search the rows already
            // loaded for that status instead.
            // Active: the Active-list cache. Inactive / All: the all-status
            // cache (the loaded rows may still be the previous filter's).
            const source = (statusFilter === 'Active' && cachedRows.length > 0)
                ? cachedRows
                : (allStatusRows.length > 0 ? allStatusRows : normalizedRows);
            next = source.filter((row) => {
                const matchesQuery = !query
                    || (row.name || '').toLowerCase().includes(query)
                    || (row.phone || '').toLowerCase().includes(query)
                    || (row.email || '').toLowerCase().includes(query)
                    || String(row.rollNumber || '').toLowerCase().includes(query);
                const matchesStatus = statusFilter === 'All' || row.status === statusFilter;
                const matchesCourseType = courseTypeFilter === 'All'
                    || (row.coursesList || []).some((c) => c.courseType === courseTypeFilter);
                return matchesQuery && matchesStatus && matchesCourseType;
            });
        }
        if (sortBy === 'alphabetic') {
            next.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortBy === 'oldest') {
            next.sort((a, b) => {
                const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : new Date((a.joinedOn || '').replace(/^Joined on /, '')).getTime();
                const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : new Date((b.joinedOn || '').replace(/^Joined on /, '')).getTime();
                return (dateA || 0) - (dateB || 0);
            });
        } else {
            next.sort((a, b) => {
                const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : new Date((a.joinedOn || '').replace(/^Joined on /, '')).getTime();
                const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : new Date((b.joinedOn || '').replace(/^Joined on /, '')).getTime();
                return (dateB || 0) - (dateA || 0);
            });
        }
        return next;
    }, [normalizedRows, cachedRows, allStatusRows, itemsQuery, search, statusFilter, courseTypeFilter, sortBy]);

    const handleExportExcel = useCallback(() => {
        if (!filteredRows || filteredRows.length === 0) {
            Notification.info('No student data to export');
            return;
        }
        const data = filteredRows.map((row) => {
            const rowObj = {};
            STUDENT_EXPORT_FIELDS.forEach((field) => {
                rowObj[field.label] = field.getValue ? field.getValue(row) : (row[field.id] || 'NA');
            });
            return rowObj;
        });
        exportToExcel(data, 'Student_List', 'Students');
    }, [filteredRows]);

    // Saved total only until the first fetch lands; after that count what the API returned (0 → "0-0 of 0").
    const total = hasFreshFetch ? filteredRows.length : (filteredRows.length || Number(studentListTotal || 0));
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const displayedRows = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredRows.slice(start, start + limit);
    }, [filteredRows, page, limit]);

    // Filter changed: bring the first rows back into view (only if the page
    // has scrolled past the table top). Re-applied while the new rows load,
    // since the browser's scroll anchoring otherwise keeps an old row in place.
    const scrollToTopUntilRef = useRef(0);
    const filterFirstRunRef = useRef(true);
    useEffect(() => {
        if (filterFirstRunRef.current) { filterFirstRunRef.current = false; return; }
        scrollToTopUntilRef.current = Date.now() + 2000;
    }, [statusFilter, courseTypeFilter]);
    useEffect(() => {
        if (Date.now() > scrollToTopUntilRef.current) return;
        // Only when scrolled past the table top.
        const { parent, delta } = getTableOffset();
        if (parent && delta < 0) parent.scrollBy({ top: delta });
    }, [displayedRows, statusFilter, courseTypeFilter]);

    const allChecked = displayedRows.length > 0 && displayedRows.every((row) => selectedIds.has(row.id));
    // "–" when some of this page is selected, or rows on other pages are.
    const someChecked = !allChecked && (displayedRows.some((row) => selectedIds.has(row.id)) || selectedIds.size > 0);

    const toggleSelectAll = () => {
        if (displayedRows.length === 0) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) {
                displayedRows.forEach((row) => next.delete(row.id));
            } else {
                displayedRows.forEach((row) => next.add(row.id));
            }
            return next;
        });
    };

    const toggleSelectRow = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Applies a successful suspend/activate/delete to the loaded list (and
    // the full-list cache) right away, so the table changes together with
    // the success snackbar instead of after the full background refetch —
    // same as StaffListTable.
    const applyStatusLocally = useCallback((ids, apiStatus) => {
        if (!ids || !ids.length) return;
        const idSet = new Set(ids.map(String));
        const matches = (item) => [item?.id, item?._id, item?.userId].some((v) => v != null && idSet.has(String(v)));
        const update = (items) => (apiStatus === 'Deleted'
            ? items.filter((item) => !matches(item))
            : items.map((item) => (matches(item) ? { ...item, status: apiStatus, userStatus: apiStatus } : item)));
        setStudentItems((prev) => update(prev));
        setAllStudentsCache((prev) => (prev.items.length ? { ...prev, items: update(prev.items) } : prev));
        setAllStatusCache((prev) => (prev.items.length ? { ...prev, items: update(prev.items) } : prev));
    }, []);

    const bulkSetStatus = (status, callback) => {
        const ids = Array.from(selectedIds);
        if (!ids.length) {
            callback?.();
            return;
        }
        bulkUpdateStudentStatus?.(ids, status, (response, error) => {
            const hasApiNotification = Boolean(response?.show || response?.data?.show);
            if (error) {
                const hasErrorNotification = Boolean(error?.show || error?.data?.show);
                if (!hasErrorNotification) {
                    const errorMsg = (typeof error === 'string' ? error : (error?.message || error?.error)) || (status === 'Deleted' ? 'Failed to delete user.' : status === 'Approved' ? 'Failed to activate user.' : 'Failed to suspend user.');
                    Notification.error('Error', errorMsg);
                }
            } else {
                const resData = response?.data ?? response;
                const failed = resData?.failed || response?.failed || [];
                const succeeded = resData?.succeeded || response?.succeeded || [];

                if (failed.length > 0 && succeeded.length === 0) {
                    if (!hasApiNotification) {
                        Notification.error(
                            'Error',
                            failed.map((f) => f.reason || (f.userId ? `User ${f.userId} failed` : 'Action failed')).join(' | ') || (status === 'Deleted' ? 'Failed to delete user.' : status === 'Approved' ? 'Failed to activate user.' : 'Failed to suspend user.')
                        );
                    }
                } else {
                    const failedIds = new Set(failed.map((f) => String(f.userId)));
                    applyStatusLocally(
                        succeeded.length > 0 ? succeeded : ids.filter((id) => !failedIds.has(String(id))),
                        status
                    );
                    if (failed.length > 0) {
                        Notification.warning(
                            'Some students could not be updated',
                            failed.map((f) => `User ${f.userId}: ${f.reason}`).join(' | ')
                        );
                    }
                    if (!hasApiNotification) {
                        if (status === 'Deleted') {
                            Notification.success('Success', 'User deleted successfully.');
                        } else if (status === 'Suspended') {
                            Notification.success('Success', 'User suspended successfully.');
                        } else if (status === 'Approved') {
                            Notification.success('Success', 'User activated successfully.');
                        }
                    }
                }
            }
            setSelectedIds(new Set());
            refetchList();
            onStudentStatusChange?.();
            callback?.();
        });
    };

    const bulkSuspend = (callback) => bulkSetStatus('Suspended', callback);

    const bulkActivate = (callback) => bulkSetStatus('Approved', callback);

    const bulkDelete = (callback) => bulkSetStatus('Deleted', callback);

    const handleAssignCourseBulk = (payload, modalCallback) => {
        const studentIds = Array.from(selectedIds).map(Number);
        if (!studentIds.length || !payload?.courseId) {
            modalCallback?.(false);
            return;
        }

        const effectiveCourseType = (payload?.courseType === 'live' ? 'course' : payload?.courseType) || 'course';
        const bulkPayload = {
            courseType: effectiveCourseType,
            courseId: Number(payload.courseId),
            students: studentIds
        };

        const activeStandardId = payload?.standardId ?? propStandardId ?? location.state?.standardId ?? location.state?.batchId;
        if (effectiveCourseType === 'course' && activeStandardId && activeStandardId !== -1 && activeStandardId !== '-1') {
            bulkPayload.standardId = Number(activeStandardId);
        }

        assignCourseBulk?.(bulkPayload, (response, error) => {
            if (error) {
                modalCallback?.(false);
                Notification.error('Failed to assign course', error?.message || 'Something went wrong');
                return;
            }

            const resData = response?.data ?? response;
            const succeeded = resData?.succeeded || [];
            const failed = resData?.failed || [];

            if (succeeded.length > 0 && failed.length === 0) {
                modalCallback?.(true);
                Notification.success('Success', `Course assigned successfully to ${succeeded.length} student${succeeded.length > 1 ? 's' : ''}`);
            } else if (succeeded.length > 0 && failed.length > 0) {
                modalCallback?.(true);
                Notification.warning(
                    'Partially Assigned',
                    `Assigned to ${succeeded.length} student(s). Failed for ${failed.length}: ${failed.map((f) => f.reason || `User ${f.userId}`).join(', ')}`
                );
            } else if (failed.length > 0) {
                modalCallback?.(false);
                Notification.error(
                    'Failed to assign course',
                    failed.map((f) => f.reason || `User ${f.userId} failed`).join(', ')
                );
            } else {
                modalCallback?.(true);
                Notification.success('Success', 'Course assigned successfully');
            }

            if (succeeded.length > 0) {
                setSelectedIds(new Set());
                refetchList();
            }
        });
    };

    const toggleRowStatus = (row, callback) => {
        setOpenMenuId(null);
        const nextStatus = toApiStatus(row.status === 'Active' ? 'Inactive' : 'Active');
        updateStudentStatus?.(row.id, nextStatus, (response, error) => {
            if (!error) applyStatusLocally([row.id], nextStatus);
            refetchList();
            onStudentStatusChange?.();
            callback?.();
        });
    };

    const deleteRow = (row, callback) => {
        setOpenMenuId(null);
        updateStudentStatus?.(row.id, 'Deleted', (response, error) => {
            if (!error) applyStatusLocally([row.id], 'Deleted');
            refetchList();
            onStudentStatusChange?.();
            callback?.();
        });
    };

    const [confirmAction, setConfirmAction] = useState(null); // 'suspend' | 'activate' | 'delete'
    const [confirmScope, setConfirmScope] = useState(null); // { type: 'row', row } | { type: 'bulk' }
    const [confirmActionLoading, setConfirmActionLoading] = useState(false);

    const requestToggleStatus = useCallback((row) => {
        setOpenMenuId(null);
        setConfirmAction(row?.status === 'Active' ? 'suspend' : 'activate');
        setConfirmScope({ type: 'row', row });
    }, []);

    const requestDeleteRow = useCallback((row) => {
        setOpenMenuId(null);
        setConfirmAction('delete');
        setConfirmScope({ type: 'row', row });
    }, []);

    const requestBulkDelete = useCallback(() => {
        if (!selectedIds.size) return;
        setConfirmAction('delete');
        setConfirmScope({ type: 'bulk' });
    }, [selectedIds]);

    const requestBulkSuspend = useCallback(() => {
        if (!selectedIds.size) return;
        setConfirmAction('suspend');
        setConfirmScope({ type: 'bulk' });
    }, [selectedIds]);

    const requestBulkActivate = useCallback(() => {
        if (!selectedIds.size) return;
        setConfirmAction('activate');
        setConfirmScope({ type: 'bulk' });
    }, [selectedIds]);

    const closeConfirmModal = useCallback(() => {
        if (confirmActionLoading) return;
        setConfirmAction(null);
        setConfirmScope(null);
    }, [confirmActionLoading]);

    const handleConfirmAction = useCallback(() => {
        if (!confirmScope || confirmActionLoading) return;
        setConfirmActionLoading(true);

        const onDone = () => {
            setConfirmActionLoading(false);
            setConfirmAction(null);
            setConfirmScope(null);
        };

        if (confirmScope.type === 'row') {
            if (confirmAction === 'delete') {
                deleteRow(confirmScope.row, onDone);
            } else {
                toggleRowStatus(confirmScope.row, onDone);
            }
        } else {
            if (confirmAction === 'delete') {
                bulkDelete(onDone);
            } else if (confirmAction === 'activate') {
                bulkActivate(onDone);
            } else {
                bulkSuspend(onDone);
            }
        }
    }, [confirmScope, confirmAction, confirmActionLoading, selectedIds, bulkDelete, bulkSuspend, bulkActivate, deleteRow, toggleRowStatus]);

    const handleViewProfile = (row) => {
        setOpenMenuId(null);
        setUserDetails?.(row);
        history.push(`${basePath}/student-profile`, {
            returnTo: `${location.pathname}${location.search}`,
            returnState: location.state,
        });
    };

    const startIndex = total ? (page - 1) * limit + 1 : 0;
    const endIndex = total ? Math.min(page * limit, total) : 0;
    const canGoPrev = page > 1 && !isLoading;
    const canGoNext = page < totalPages && !isLoading;

    const isInactiveFilter = statusFilter === 'Inactive' || (selectedIds.size > 0 && filteredRows.filter((r) => selectedIds.has(r.id)).length > 0 && filteredRows.filter((r) => selectedIds.has(r.id)).every((r) => r.status === 'Inactive'));

    const activeStandardId = propStandardId ?? location.state?.standardId ?? location.state?.batchId;
    const isSectionContext = Boolean(
        (activeStandardId && activeStandardId !== -1 && activeStandardId !== '-1') ||
        location.pathname?.includes('/batches/student-list')
    );
    const hasSearchOrFilter = Boolean(search && search.trim()) || statusFilter !== 'Active' || courseTypeFilter !== 'All';
    const emptyMessage = (isSectionContext && !hasSearchOrFilter)
        ? 'No students in this section'
        : 'No student match your search/filters.';

    // Pager (‹ 1-20 of N ▾ ›) — shown in the normal toolbar and, while rows
    // are selected, after the bulk-action buttons.
    const pagerNode = (
        <div style={{ display: 'flex', alignItems: 'center', gap: fluid(6), height: fluid(40), boxSizing: 'border-box', padding: `0 ${fluid(8)}`, borderRadius: fluid(1000), backgroundColor: '#F7F7F7', fontSize: fluid(18), lineHeight: fluid(24), fontWeight: 400, color: '#666666', whiteSpace: 'nowrap' }}>
            <button
                type="button"
                disabled={!canGoPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ width: fluid(24), height: fluid(24), border: 'none', borderRadius: '50%', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canGoPrev ? 'pointer' : 'not-allowed', opacity: canGoPrev ? 1 : 0.4, padding: 0 }}
            >
                <img src={ArrowLeft} alt="Previous page" style={{ width: fluid(24), height: fluid(24) }} />
            </button>
            <PageSizeRange
                label={total ? `${startIndex}-${endIndex} of ${total.toLocaleString()}` : '0-0 of 0'}
                pageSize={limit}
                onChange={(n) => { setLimit(n); setPage(1); }}
            />
            <button
                type="button"
                disabled={!canGoNext}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{ width: fluid(24), height: fluid(24), border: 'none', borderRadius: '50%', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canGoNext ? 'pointer' : 'not-allowed', opacity: canGoNext ? 1 : 0.4, padding: 0 }}
            >
                <img src={ArrowRight} alt="Next page" style={{ width: fluid(24), height: fluid(24) }} />
            </button>
        </div>
    );

    return (
        <div ref={tableRootRef} style={{ backgroundColor: '#FFFFFF', borderRadius: fluid(24), width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, fontFamily: 'Roboto, sans-serif', position: 'relative' }}>
            <PageDataLoader visible={isLoading && initialLoadDone} />
            <div style={{ position: isMobile ? 'static' : 'sticky', top: isMobile ? undefined : 0, zIndex: 20, backgroundColor: '#FFFFFF', borderRadius: `${fluid(24)} ${fluid(24)} 0 0`, flexShrink: 0 }}>
                <div style={{ padding: isMobile ? '18px 18px 0' : `${fluid(24)} ${fluid(28)} 0`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: fluid(14), paddingBottom: fluid(18) }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: fluid(4) }}>
                            <span style={{ fontSize: fluid(20), fontWeight: 700, color: '#191919' }}>
                                Students list <span style={{ color: '#AEAEAE', fontWeight: 500 }}>({total.toLocaleString()})</span>
                            </span>
                            {/* Grid view shows the count in "Select all (N selected)". */}
                            {selectedIds.size > 0 && viewMode === 'list' && (
                                <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(14), fontWeight: 500, color: '#636363' }}>
                                    Selected {selectedIds.size} {selectedIds.size === 1 ? 'student' : 'students'}
                                </span>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: fluid(10), flexWrap: 'wrap' }}>
                            {selectedIds.size > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: fluid(12), flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        onClick={() => setAssignCourseModalOpen(true)}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: fluid(40),
                                            lineHeight: 1,
                                            padding: `0 ${fluid(24)}`,
                                            boxSizing: 'border-box',
                                            borderRadius: fluid(12),
                                            border: '2px solid #594099',
                                            backgroundColor: 'transparent',
                                            color: '#594099',
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, transform: 'translateY(1px)' }}>
                                            Assign course
                                        </span>
                                    </button>
                                    {isInactiveFilter ? (
                                        <button
                                            type="button"
                                            onClick={requestBulkActivate}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: fluid(40),
                                                lineHeight: 1,
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: 'border-box',
                                                borderRadius: fluid(12),
                                                border: '2px solid #008A67',
                                                backgroundColor: 'transparent',
                                                color: '#008A67',
                                                fontFamily: 'Roboto, sans-serif',
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, transform: 'translateY(1px)' }}>
                                                Activate
                                            </span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={requestBulkSuspend}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: fluid(40),
                                                lineHeight: 1,
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: 'border-box',
                                                borderRadius: fluid(12),
                                                border: '2px solid #FFA931',
                                                backgroundColor: 'transparent',
                                                color: '#FFA931',
                                                fontFamily: 'Roboto, sans-serif',
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, transform: 'translateY(1px)' }}>
                                                Suspend
                                            </span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={requestBulkDelete}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: fluid(40),
                                            lineHeight: 1,
                                            padding: `0 ${fluid(24)}`,
                                            boxSizing: 'border-box',
                                            borderRadius: fluid(12),
                                            border: '2px solid #FF414D',
                                            backgroundColor: 'transparent',
                                            color: '#FF414D',
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, transform: 'translateY(1px)' }}>
                                            Delete
                                        </span>
                                    </button>
                                    {pagerNode}
                                </div>
                            ) : (
                                <>
                                    <div style={{ position: 'relative' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: fluid(12), padding: `0 ${fluid(16)}`, height: fluid(40), borderRadius: fluid(1000), backgroundColor: '#FFFFFF', border: '1px solid #B3B3B3', width: isMobile ? '220px' : fluid(400), boxSizing: 'border-box' }}>
                                            <FiSearch size={18} color={COLORS.placeholderGray || '#B3B3B3'} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                                            <input
                                                type="text"
                                                value={search}
                                                onChange={(e) => {
                                                    setSearch(e.target.value);
                                                    setPage(1);
                                                }}
                                                onFocus={scrollTableIntoView}
                                                placeholder="Search by name, phone No., email, roll No."
                                                style={{ border: 'none', outline: 'none', width: '100%', fontSize: fluid(16), color: '#191919', fontFamily: 'Roboto, sans-serif' }}
                                            />
                                            {search && (
                                                <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', flexShrink: 0 }}>
                                                    <FiX size={18} color={COLORS.placeholderGray || '#B3B3B3'} strokeWidth={1.5} />
                                                </button>
                                            )}
                                        </div>

                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', height: fluid(40), borderRadius: fluid(1000), backgroundColor: '#F1F1F1', boxSizing: 'border-box' }}>
                                        <div ref={filterRef} style={{ position: 'relative' }}>
                                            <button type="button" onClick={() => { scrollTableIntoView().then(openFilterPopover); }} style={{ width: fluid(46), height: fluid(40), borderRadius: `${fluid(1000)} ${fluid(8)} ${fluid(8)} ${fluid(1000)}`, border: 'none', backgroundColor: filterOpen ? COLORS.pillBg : '#F1F1F1', display: 'flex', alignItems: 'center', padding: `${fluid(10)} ${fluid(10)} ${fluid(10)} ${fluid(16)}`, boxSizing: 'border-box', cursor: 'pointer' }}>
                                                <img src={FilterIcon} alt="Filter" style={{ width: fluid(20), height: fluid(20) }} />
                                            </button>
                                            {filterOpen && (
                                                <div ref={filterPanelRef} style={{ position: 'absolute', top: fluid(48), right: 0, minWidth: fluid(360), backgroundColor: '#FFFFFF', borderRadius: fluid(20), boxShadow: '0 12px 32px rgba(0,0,0,0.16)', padding: fluid(20), zIndex: 30, boxSizing: 'border-box' }}>
                                                    <div style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 700, color: '#191919', marginBottom: fluid(12) }}>Status</div>
                                                    <PillGroup options={STATUS_PILLS} value={draftStatusFilter} onChange={setDraftStatusFilter} />
                                                    <div style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 700, color: '#191919', marginTop: fluid(20), marginBottom: fluid(12) }}>Course type</div>
                                                    <PillGroup options={COURSE_TYPE_PILLS} value={draftCourseTypeFilter} onChange={setDraftCourseTypeFilter} />
                                                    <PopoverFooter disabled={isFilterDisabled} onReset={resetFilterPopover} onApply={applyFilterPopover} />
                                                </div>
                                            )}
                                        </div>

                                        <div ref={sortRef} style={{ position: 'relative' }}>
                                            <button type="button" onClick={() => { scrollTableIntoView().then(openSortPopover); }} style={{ width: fluid(40), height: fluid(40), border: 'none', backgroundColor: sortOpen ? COLORS.pillBg : '#F1F1F1', borderRadius: fluid(8), display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                                <img src={SortIcon} alt="Sort" style={{ width: fluid(20), height: fluid(20) }} />
                                            </button>
                                            {sortOpen && (
                                                <div ref={sortPanelRef} style={{ position: 'absolute', top: fluid(48), right: 0, minWidth: fluid(360), backgroundColor: '#FFFFFF', borderRadius: fluid(20), boxShadow: '0 12px 32px rgba(0,0,0,0.16)', padding: fluid(20), zIndex: 30, boxSizing: 'border-box' }}>
                                                    <div style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 700, color: '#191919', marginBottom: fluid(12) }}>Sort by</div>
                                                    <PillGroup options={SORT_PILLS} value={draftSortBy} onChange={setDraftSortBy} />
                                                    <PopoverFooter disabled={isSortDisabled} onReset={resetSortPopover} onApply={applySortPopover} />
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => setDownloadModalOpen(true)}
                                            title="Export Excel"
                                            style={{
                                                width: fluid(46),
                                                height: fluid(40),
                                                borderRadius: `${fluid(8)} ${fluid(1000)} ${fluid(1000)} ${fluid(8)}`,
                                                border: 'none',
                                                backgroundColor: '#F1F1F1',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                padding: 0,
                                            }}
                                        >
                                            <img src={DownloadIcon} alt="Export" style={{ width: fluid(20), height: fluid(20) }} />
                                        </button>
                                    </div>

                                    {pagerNode}

                                    <button
                                        type="button"
                                        onClick={() => { setViewMode((m) => (m === 'list' ? 'grid' : 'list')); setHoveredCardId(null); }}
                                        title={viewMode === 'list' ? 'Grid view' : 'List view'}
                                        style={{ width: fluid(40), height: fluid(40), borderRadius: fluid(8), border: 'none', outline: 'none', appearance: 'none', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: fluid(4), boxSizing: 'border-box', boxShadow: 'none', cursor: 'pointer', flexShrink: 0 }}
                                    >
                                        <img src={viewMode === 'list' ? GridIcon : ListIcon} alt={viewMode === 'list' ? 'Grid view' : 'List view'} style={{ width: fluid(32), height: fluid(32) }} />
                                    </button>

                                    {canAddStudent && (
                                        <button
                                            type="button"
                                            onClick={onAddStudent}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: fluid(40),
                                                padding: `0 ${fluid(24)}`,
                                                backgroundColor: '#594099',
                                                color: '#FFFFFF',
                                                border: 'none',
                                                borderRadius: fluid(12),
                                                fontSize: fluid(16),
                                                fontWeight: 700,
                                                fontFamily: 'Roboto, sans-serif',
                                                lineHeight: "normal",
                                                cursor: 'pointer',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            ADD STUDENT
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {viewMode === 'grid' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: fluid(12), height: fluid(42), boxSizing: 'border-box', margin: isMobile ? '0 18px' : `0 ${fluid(24)}`, flexShrink: 0 }}>
                        <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleSelectAll} ariaLabel="Select all" disabled={displayedRows.length === 0} />
                        <span style={{ fontSize: fluid(14), fontWeight: 400, color: '#636363', fontFamily: 'Roboto, sans-serif' }}>
                            Select all{selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ''}
                        </span>
                    </div>
                )}
                {viewMode === 'list' && (
                    <div style={{ display: 'grid', gridTemplateColumns: COLUMN_TEMPLATE, alignItems: 'center', gap: fluid(12), margin: isMobile ? '0 18px' : `0 ${fluid(25)}`, padding: `${fluid(10)} ${fluid(20)}`, backgroundColor: '#F4F4F4', borderRadius: `${fluid(16)} ${fluid(16)} 0 0`, flexShrink: 0 }}>
                        <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleSelectAll} ariaLabel="Select all" disabled={displayedRows.length === 0} />
                        <span style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 400, color: '#1A1A1A', fontFamily: 'Roboto, sans-serif' }}>Student</span>
                        <span style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 400, color: '#1A1A1A', fontFamily: 'Roboto, sans-serif' }}>Contact</span>
                        <span style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 400, color: '#1A1A1A', fontFamily: 'Roboto, sans-serif' }}>Course | Batch | Roll No.</span>
                        <span style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 400, color: '#1A1A1A', fontFamily: 'Roboto, sans-serif' }}>Status</span>
                        <span style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 400, color: '#1A1A1A', fontFamily: 'Roboto, sans-serif', textAlign: 'right' }}>Actions</span>
                    </div>
                )}
            </div>

            {/* overflowAnchor none: expanding "+N more" must not make the browser
                scroll to keep a row's centred content in place (that pushed the
                row's first course under the sticky header). */}
            <div ref={rowsRegionRef} className="dv2-rows-region" style={viewMode === 'grid'
                ? { overflowAnchor: 'none', margin: isMobile ? '12px 18px 16px' : `${fluid(12)} ${fluid(24)} ${fluid(16)}`, boxSizing: 'border-box' }
                : { overflowAnchor: 'none', overflowY: 'visible', overflowX: isMobile ? 'auto' : 'visible', margin: isMobile ? '0 18px 16px' : `0 ${fluid(25)} ${fluid(16)}`, backgroundColor: '#FFFFFF', borderRadius: `0 0 ${fluid(16)} ${fluid(16)}`, borderLeft: '1px solid #E6E6E6', borderRight: '1px solid #E6E6E6', borderBottom: '1px solid #E6E6E6', boxSizing: 'border-box' }}>
                {/* First load after opening / coming back: a loader instead of an old copy of the list. */}
                {!initialLoadDone ? (
                    <div ref={initialLoaderRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: fluid(260), height: initialLoaderHeight, width: '100%' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                <>
                {/* Grid: at least 358px per card, then stretch so the row fills the full width. */}
                {viewMode === 'grid' && displayedRows.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(auto-fill, minmax(${fluid(358)}, 1fr))`, gap: fluid(24), paddingBottom: fluid(24), boxSizing: 'border-box' }}>
                        {displayedRows.map((row) => (
                            <StudentGridCard
                                key={row.id}
                                row={row}
                                checked={selectedIds.has(row.id)}
                                // No hover expansion while Filter / Sort is open — the expanded
                                // card would cover the popover's options.
                                hovered={!filterOpen && !sortOpen && hoveredCardId === row.id}
                                onHoverEnter={() => setHoveredCardId(row.id)}
                                onHoverLeave={() => setHoveredCardId(null)}
                                onToggleCheck={toggleSelectRow}
                                onToggleStatus={requestToggleStatus}
                                onDelete={requestDeleteRow}
                                onViewProfile={handleViewProfile}
                                actionsDisabled={selectedIds.size > 0}
                            />
                        ))}
                    </div>
                )}
                {viewMode === 'list' && displayedRows.map((row, index) => {
                    const isRowSelected = selectedIds.has(row.id);
                    const isLast = index === displayedRows.length - 1;
                    return (
                        <div key={row.id} style={{ position: 'relative' }}>
                            {/* Only the student name navigates to the profile — no whole-row click. */}
                            {/* 10px top/bottom: an expanded "+N more" row keeps the same gap
                                below the header as a collapsed one (was 6px). */}
                            <div style={{ display: 'grid', gridTemplateColumns: COLUMN_TEMPLATE, alignItems: 'center', gap: fluid(12), minHeight: '72px', padding: `10px ${fluid(20)}`, boxSizing: 'border-box' }}>
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSelectRow(row.id);
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '100%',
                                        alignSelf: 'stretch',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Checkbox
                                        checked={isRowSelected}
                                        onChange={(e) => {
                                            if (e && e.stopPropagation) e.stopPropagation();
                                            toggleSelectRow(row.id);
                                        }}
                                        ariaLabel={`Select ${row.name}`}
                                    />
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                                    <Avatar name={row.name} image={row.avatar} />
                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <span onClick={() => handleViewProfile(row)} style={{ fontSize: '13px', lineHeight: '24px', fontWeight: 400, color: '#191919', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Roboto, sans-serif', cursor: 'pointer' }}>
                                            {row.name}
                                        </span>
                                        <span style={{ fontSize: '13px', lineHeight: '24px', fontWeight: 400, color: '#AEAEAE', whiteSpace: 'nowrap', fontFamily: 'Roboto, sans-serif' }}>
                                            {row.joinedOn}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                    <span style={{ fontSize: '13px', lineHeight: '24px', fontWeight: 400, color: '#191919', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Roboto, sans-serif' }}>
                                        {formatContactPhone(row.phone)}
                                    </span>
                                    {row.email && (
                                        <span style={{ fontSize: '13px', lineHeight: '24px', fontWeight: 400, color: '#AEAEAE', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Roboto, sans-serif' }}>
                                            {row.email}
                                        </span>
                                    )}
                                </div>

                                <CourseBatchCell
                                    row={row}
                                    isExpanded={!!expandedRows[row.id]}
                                    onToggleExpand={(e) => toggleExpandRow(row.id, e)}
                                />

                                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '22px', padding: '0 10px', borderRadius: '4px', backgroundColor: row.status === 'Active' ? '#ECFDF5' : row.status === 'Deleted' ? '#FEE2E2' : '#FFFBEB', border: `1px solid ${row.status === 'Active' ? '#34D399' : row.status === 'Deleted' ? '#FCA5A5' : '#FBBF24'}`, color: row.status === 'Active' ? '#059669' : row.status === 'Deleted' ? '#B91C1C' : '#D97706', fontSize: '12px', fontWeight: row.status === 'Active' ? 600 : 500, lineHeight: '18px', boxSizing: 'border-box', whiteSpace: 'nowrap', fontFamily: 'Roboto, sans-serif' }}>
                                        {row.status}
                                    </span>
                                </div>

                                {/* Checked row: toolbar actions apply — its own menu is locked (like StaffListTable). */}
                                <div style={{ position: 'relative', justifySelf: 'end' }}>
                                    <button type="button" disabled={selectedIds.has(row.id)} onClick={(e) => { e.stopPropagation(); if (selectedIds.has(row.id)) return; toggleRowMenu(row.id, e.currentTarget); }} style={{ width: fluid(44), height: fluid(44), border: 'none', borderRadius: fluid(12), backgroundColor: openMenuId === row.id ? '#F1F1F1' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: selectedIds.has(row.id) ? 'not-allowed' : 'pointer', opacity: selectedIds.has(row.id) ? 0.4 : 1 }}>
                                        <FiMoreVertical size={24} color="#666666" strokeWidth={2} />
                                    </button>
                                    {openMenuId === row.id && (
                                        <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', ...(menuOpenUp ? { bottom: fluid(48) } : { top: fluid(48) }), right: 0, minWidth: fluid(140), backgroundColor: '#FFFFFF', borderRadius: fluid(16), boxShadow: '0 6px 20px rgba(0,0,0,0.12)', padding: fluid(8), zIndex: 30 }}>
                                            <div onClick={() => handleViewProfile(row)} className="staff-row-menu-item" style={{ display: 'flex', alignItems: 'center', padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: '#5A5A5A', cursor: 'pointer' }}>
                                                View
                                            </div>
                                            <div onClick={() => requestToggleStatus(row)} className="staff-row-menu-item" style={{ display: 'flex', alignItems: 'center', padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: row.status === 'Active' ? '#D97706' : '#008A67', cursor: 'pointer' }}>
                                                {row.status === 'Active' ? 'Suspend' : 'Activate'}
                                            </div>
                                            <div onClick={() => requestDeleteRow(row)} className="staff-row-menu-item" style={{ display: 'flex', alignItems: 'center', padding: `${fluid(10)} ${fluid(12)}`, fontSize: fluid(16), color: '#FF0010', cursor: 'pointer' }}>
                                                Delete
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!isLast && <div style={{ position: 'absolute', left: fluid(24), right: fluid(24), bottom: 0, height: '1px', backgroundColor: '#E6E6E6' }} />}
                        </div>
                    );
                })}

                {filteredRows.length === 0 && !isLoading && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `${fluid(48)} 0`,
                        width: '100%',
                        minHeight: fluid(260),
                    }}>
                        <img src={illNoData} style={{ width: fluid(220) }} alt={emptyMessage} />
                        <span style={{
                            fontSize: fluid(18),
                            fontWeight: 500,
                            color: '#636363',
                            marginTop: fluid(16),
                            fontFamily: 'Roboto, sans-serif'
                        }}>
                            {emptyMessage}
                        </span>
                    </div>
                )}
                </>
                )}



                <EnrollCourseModal
                    visible={assignCourseModalOpen}
                    onClose={() => setAssignCourseModalOpen(false)}
                    onEnroll={handleAssignCourseBulk}
                    isBulk={true}
                    selectedCount={selectedIds.size}
                />

                <UserActionConfirmModal
                    open={!!confirmScope}
                    onCancel={closeConfirmModal}
                    onConfirm={handleConfirmAction}
                    action={confirmAction || 'suspend'}
                    loading={confirmActionLoading}
                    isBulk={confirmScope?.type === 'bulk' ? selectedIds.size > 1 : false}
                />

                <DownloadFileModal
                    open={downloadModalOpen}
                    onClose={() => setDownloadModalOpen(false)}
                    fields={STUDENT_EXPORT_FIELDS}
                    rows={filteredRows}
                    allRows={allStatusRows.length ? allStatusRows : normalizedRows}
                    entityLabel="students"
                    fileName="Student_List"
                    sheetName="Students"
                    getDate={(row) => row.joinedAt || row.joinedOn}
                />
            </div>
        </div>
    );
});

StudentListTable.displayName = 'StudentListTable';

const mapStateToProps = (state) => ({
    studentList: state?.studentManagement?.studentList || [],
    studentListTotal: state?.studentManagement?.studentListTotal || 0,
    lastAddedStudent: state?.studentManagement?.lastAddedStudent || null,
    marketSettingData: state?.studentMarketPlace?.marketSettingData,
});

const mapDispatchToProps = (dispatch) => ({
    getStudentList: (params, callback) => dispatch(getStudentList(params, callback)),
    getStudentAutocomplete: (params, callback) => dispatch(getStudentAutocomplete(params, callback)),
    updateStudentStatus: (userId, status, callback) => dispatch(updateStudentStatus(userId, status, callback)),
    bulkUpdateStudentStatus: (userIds, status, callback) => dispatch(bulkUpdateStudentStatus(userIds, status, callback)),
    assignCourseBulk: (params, callback) => dispatch(assignCourseBulk(params, callback)),
    setUserDetails: (row) => dispatch(setUserDetails(row)),
    getMarketSettingsData: (all, callback) => dispatch(getMarketSettingsData(all, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentListTable);
